/* eslint-disable camelcase -- matches React Router's export name */
import * as React from 'react'
import { UNSAFE_DataRouterContext, useBlocker } from 'react-router'

type PromptProps = {
  when?: boolean
  message: string | (() => string)
}

function PromptDataRouter({ when, message }: PromptProps) {
  const resolvedMessage = typeof message === 'function' ? message() : message
  const active = when !== false
  const blocker = useBlocker(active)
  React.useEffect(() => {
    if (blocker.state === 'blocked') {
      // eslint-disable-next-line no-alert -- intentional v5 Prompt parity (same as RR unstable_usePrompt)
      const proceed = window.confirm(resolvedMessage)
      if (proceed) {
        setTimeout(blocker.proceed, 0)
      } else {
        blocker.reset()
      }
    }
  }, [blocker, resolvedMessage])
  React.useEffect(() => {
    if (blocker.state === 'blocked' && !active) {
      blocker.reset()
    }
  }, [blocker, active])
  return null
}

function PromptBeforeUnload({ when, message }: PromptProps) {
  const resolvedMessage = typeof message === 'function' ? message() : message
  const active = when !== false
  React.useEffect(() => {
    if (!active) {
      return undefined
    }
    const onBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault()
      e.returnValue = resolvedMessage
    }
    window.addEventListener('beforeunload', onBeforeUnload)
    return () => window.removeEventListener('beforeunload', onBeforeUnload)
  }, [active, resolvedMessage])
  return null
}

/**
 * v5-style unsaved-changes prompt.
 *
 * Do not use `react-router-dom`'s `unstable_usePrompt` here: under Jest + CJS it is often
 * undefined (interop / circular load via the `react-router` shim).
 *
 * Data router (`RouterProvider` / `createMemoryRouter`): `useBlocker` + confirm. The Lister app root
 * uses `createBrowserRouter` + `RouterProvider`, so production SPA navigations hit this path. We
 * detect the data router via `UNSAFE_DataRouterContext` — React Router's context for "running under
 * a data router". The `UNSAFE_` prefix marks API stability (may change across minors), not security;
 * `useBlocker` throws if used outside a data router, so we must branch. See
 * https://reactrouter.com/en/main/hooks/use-blocker
 *
 * `MemoryRouter` / other non-data routers: `beforeunload` only when `when` is true (tab close /
 * full reload).
 */
export function Prompt(props: PromptProps) {
  const dataRouter = React.useContext(UNSAFE_DataRouterContext)
  if (dataRouter != null) {
    return <PromptDataRouter {...props} />
  }
  return <PromptBeforeUnload {...props} />
}
